import { useState, useEffect } from 'react';
import { Shield, Lock, Eye, FileText, CheckCircle, Award, Zap, Users, Globe, Server, Database, Wifi } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface GDPRComplianceProps {
  variant?: 'footer' | 'dashboard' | 'floating';
  className?: string;
}

export function GDPRCompliance({ variant = 'footer', className }: GDPRComplianceProps) {
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const allBadges = [
    {
      icon: Shield,
      label: 'GDPR Compliant',
      description: 'EU Data Protection Regulation',
      color: 'bg-green-100 text-green-800 border-green-200',
      iconColor: 'text-green-600'
    },
    {
      icon: Lock,
      label: 'Data Secure',
      description: 'End-to-end encryption',
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      iconColor: 'text-blue-600'
    },
    {
      icon: Eye,
      label: 'Privacy First',
      description: 'Your data, your control',
      color: 'bg-purple-100 text-purple-800 border-purple-200',
      iconColor: 'text-purple-600'
    },
    {
      icon: CheckCircle,
      label: 'ISO 27001',
      description: 'Information security standard',
      color: 'bg-orange-100 text-orange-800 border-orange-200',
      iconColor: 'text-orange-600'
    },
    {
      icon: Award,
      label: 'SOC 2 Type II',
      description: 'Security & availability controls',
      color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      iconColor: 'text-indigo-600'
    },
    {
      icon: Zap,
      label: 'PCI DSS',
      description: 'Payment card industry standard',
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      iconColor: 'text-yellow-600'
    },
    {
      icon: Users,
      label: 'HIPAA Ready',
      description: 'Healthcare data protection',
      color: 'bg-pink-100 text-pink-800 border-pink-200',
      iconColor: 'text-pink-600'
    },
    {
      icon: Globe,
      label: 'Privacy Shield',
      description: 'Cross-border data transfers',
      color: 'bg-teal-100 text-teal-800 border-teal-200',
      iconColor: 'text-teal-600'
    },
    {
      icon: Server,
      label: 'Cloud Secure',
      description: 'Enterprise-grade infrastructure',
      color: 'bg-slate-100 text-slate-800 border-slate-200',
      iconColor: 'text-slate-600'
    },
    {
      icon: Database,
      label: 'Data Residency',
      description: 'Regional data storage compliance',
      color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      iconColor: 'text-emerald-600'
    },
    {
      icon: Wifi,
      label: 'Zero Trust',
      description: 'Never trust, always verify',
      color: 'bg-red-100 text-red-800 border-red-200',
      iconColor: 'text-red-600'
    }
  ];

  // For dashboard floating badges - cycle through badges
  useEffect(() => {
    if (variant === 'dashboard') {
      const interval = setInterval(() => {
        setIsVisible(false);
        
        setTimeout(() => {
          setCurrentBadgeIndex((prev) => (prev + 1) % allBadges.length);
          setIsVisible(true);
        }, 300);
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [variant, allBadges.length]);

  // For footer, use first 4 badges
  const badges = variant === 'footer' ? allBadges.slice(0, 4) : allBadges;

  if (variant === 'dashboard') {
    const currentBadge = allBadges[currentBadgeIndex];
    const nextBadge = allBadges[(currentBadgeIndex + 1) % allBadges.length];
    
    return (
      <Card className={cn("bg-gradient-to-br from-slate-50 to-white border-slate-200 shadow-sm overflow-hidden", className)}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={cn(
                "transition-all duration-300 ease-in-out",
                isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
              )}>
                <currentBadge.icon className="h-4 w-4 text-slate-600" />
              </div>
              <div className="relative overflow-hidden">
                <div className={cn(
                  "transition-all duration-300 ease-in-out",
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
                )}>
                  <span className="text-sm font-medium text-slate-700">{currentBadge.label}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className={cn(
                "transition-all duration-300 ease-in-out",
                isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
              )}>
                <Badge variant="outline" className="text-xs bg-slate-900 text-white border-slate-900 transition-colors duration-300">
                  <currentBadge.icon className="h-3 w-3 mr-1" />
                  Active
                </Badge>
              </div>
              <div className="opacity-30">
                <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600 border-slate-200">
                  <nextBadge.icon className="h-3 w-3 mr-1" />
                  Next
                </Badge>
              </div>
            </div>
          </div>
          <div className="mt-2 relative overflow-hidden">
            <div className={cn(
              "transition-all duration-300 ease-in-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
            )}>
              <p className="text-xs text-slate-500">{currentBadge.description}</p>
            </div>
          </div>
          
          {/* Progress indicator */}
          <div className="mt-3 flex items-center space-x-1">
            {allBadges.map((_, index) => (
              <div
                key={index}
                className={cn(
                  "h-1 rounded-full transition-all duration-300",
                  index === currentBadgeIndex 
                    ? "bg-slate-400 w-3" 
                    : "bg-slate-200 w-1"
                )}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (variant === 'floating') {
    const currentBadge = allBadges[currentBadgeIndex];
    
    return (
      <div className={cn(
        "fixed bottom-4 right-4 z-40 transition-all duration-500 ease-in-out",
        isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-95",
        className
      )}>
        <div className={cn(
          "flex items-center space-x-2 px-4 py-2 rounded-full border shadow-lg backdrop-blur-sm",
          "hover:shadow-xl hover:scale-105 cursor-pointer transition-all duration-200",
          "bg-white bg-opacity-90 border-slate-200 text-slate-700"
        )}>
          <currentBadge.icon className="h-4 w-4 text-slate-600" />
          <span className="text-sm font-medium">{currentBadge.label}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-3", className)}>
      {badges.map((badge, index) => (
        <div
          key={index}
          className={cn(
            "flex items-center space-x-2 px-3 py-1.5 rounded-full border transition-all duration-200",
            "hover:shadow-md hover:scale-105 cursor-pointer",
            badge.color
          )}
          title={badge.description}
        >
          <badge.icon className={cn("h-3.5 w-3.5", badge.iconColor)} />
          <span className="text-xs font-medium">{badge.label}</span>
        </div>
      ))}
      <div className="flex items-center space-x-2 text-xs text-muted-foreground">
        <FileText className="h-3 w-3" />
        <a 
          href="/privacy-policy" 
          className="hover:text-primary transition-colors underline-offset-4 hover:underline"
        >
          Privacy Policy
        </a>
        <span>•</span>
        <a 
          href="/cookie-policy" 
          className="hover:text-primary transition-colors underline-offset-4 hover:underline"
        >
          Cookie Policy
        </a>
        <span>•</span>
        <a 
          href="/data-rights" 
          className="hover:text-primary transition-colors underline-offset-4 hover:underline"
        >
          Your Rights
        </a>
      </div>
    </div>
  );
} 